import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../../store/store.ts';
import { TmAssessment } from '../../models/tmAssessment.ts';

const STORAGE_KEY = 'tmSelectedAssessmentId';

const readStoredAssessmentId = (): number | null => {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

const persistSelectedAssessment = (id: number | null) => {
  if (typeof window === 'undefined') return;
  if (id === null) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, id.toString());
  }
};

interface TmAssessmentState {
  tmAssessments: TmAssessment[];
  selectedTmAssessmentId: number | null;
}

const initialState: TmAssessmentState = {
  tmAssessments: [],
  selectedTmAssessmentId: readStoredAssessmentId(),
};

const tmAssessmentSlice = createSlice({
  name: 'tmAssessment',
  initialState,
  reducers: {
    setTmAssessments(state, action: PayloadAction<TmAssessment[]>) {
      state.tmAssessments = action.payload;

      const hasCurrentSelection = action.payload.some(
        assessment => assessment.id === state.selectedTmAssessmentId,
      );

      if (!hasCurrentSelection) {
        state.selectedTmAssessmentId = action.payload[0]?.id ?? null;
        persistSelectedAssessment(state.selectedTmAssessmentId);
      }
    },
    addTmAssessment(state, action: PayloadAction<TmAssessment>) {
      state.tmAssessments.push(action.payload);
      if (action.payload.id !== undefined) {
        state.selectedTmAssessmentId = action.payload.id;
        persistSelectedAssessment(state.selectedTmAssessmentId);
      }
    },
    updateTmAssessment(state, action: PayloadAction<TmAssessment>) {
      const index = state.tmAssessments.findIndex(a => a.id === action.payload.id);
      if (index !== -1) state.tmAssessments[index] = action.payload;
    },
    removeTmAssessment(state, action: PayloadAction<number>) {
      state.tmAssessments = state.tmAssessments.filter(a => a.id !== action.payload);
      if (state.selectedTmAssessmentId === action.payload) {
        state.selectedTmAssessmentId = state.tmAssessments[0]?.id ?? null;
        persistSelectedAssessment(state.selectedTmAssessmentId);
      }
    },
    setSelectedTmAssessmentId(state, action: PayloadAction<number | null>) {
      state.selectedTmAssessmentId = action.payload;
      persistSelectedAssessment(state.selectedTmAssessmentId);
    },
  },
});

export const {
  setTmAssessments,
  addTmAssessment,
  updateTmAssessment,
  removeTmAssessment,
  setSelectedTmAssessmentId,
} = tmAssessmentSlice.actions;

export const selectTmAssessments = (state: RootState) => state.tmAssessment.tmAssessments;
export const selectSelectedTmAssessmentId = (state: RootState) =>
  state.tmAssessment.selectedTmAssessmentId;
export const selectSelectedTmAssessment = (state: RootState) => {
  const id = state.tmAssessment.selectedTmAssessmentId;
  if (!id) return null;
  return state.tmAssessment.tmAssessments.find(a => a.id === id) ?? null;
};

export default tmAssessmentSlice.reducer;
